import React from "react";

/** Section card wrapper — white surface, rounded, subtle shadow. */
export function Card({
  children,
  className = ""
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <section className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`} data-id="element-153" data-mp="37ndmq7wxdyee">
      {children}
    </section>;
}
export function CardHeader({
  title,
  subtitle,
  icon,
  action
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4" data-id="element-154" data-mp="2a0ey8kgdzwrl">
      <div className="flex items-start gap-3" data-id="element-155" data-mp="2nzo4osesjcid">
        {icon && <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-[#1a73e8]" data-id="element-156" data-mp="2jwrt3lno4gnx">
            {icon}
          </span>}
        <div data-id="element-157" data-mp="3ai85x7ngh48">
          <h2 className="text-sm font-semibold text-slate-900" data-id="element-158" data-mp="3vcgc61xswvjg">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-slate-500" data-id="element-159" data-mp="3l6o14yb3svze">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>;
}

/** Soft amber "Needs info" pill for empty important fields. */
export function NeedsInfoPill() {
  return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200" data-id="element-160" data-mp="3u8dont1v7arw">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" data-id="element-161" data-mp="32gg1iq6m4nny" />
      Needs info
    </span>;
}
type BadgeTone = "blue" | "green" | "amber" | "slate";
const badgeTones: Record<BadgeTone, string> = {
  blue: "bg-blue-50 text-[#1a73e8] ring-blue-200",
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  slate: "bg-slate-100 text-slate-600 ring-slate-200"
};
export function Badge({
  children,
  tone = "slate",
  icon
}: {
  children: React.ReactNode;
  tone?: BadgeTone;
  icon?: React.ReactNode;
}) {
  return <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${badgeTones[tone]}`} data-id="element-162" data-mp="3r1nzjvr26v32">
      {icon}
      {children}
    </span>;
}

/**
 * Guided editable field. Shows label, value (or Needs info pill when empty),
 * and an optional highlight for partially-filled fields that matter.
 */
export function Field({
  label,
  value,
  type = "text",
  highlight = false,
  helper
}: {
  label: string;
  value: string;
  type?: string;
  highlight?: boolean;
  helper?: string;
}) {
  const empty = value.trim() === "";
  return <div data-id="element-163" data-mp="3h3hz7ikwhx0b">
      <div className="mb-1 flex items-center justify-between gap-2" data-id="element-164" data-mp="3h49jhx91s8ba">
        <label className="text-xs font-medium text-slate-500" data-id="element-165" data-mp="3h1dmcb50xnnw">{label}</label>
        {empty && <NeedsInfoPill data-id="element-166" data-mp="3c6rj4v93j1ew" />}
      </div>
      <input type={type} defaultValue={value} placeholder={empty ? "—" : undefined} className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-300 focus:border-[#1a73e8] focus:ring-2 focus:ring-blue-100 ${empty && highlight ? "border-amber-300 bg-amber-50/40" : "border-slate-200 hover:border-slate-300"}`} data-id="element-167" data-mp="wczmyv74oy25" />
      {helper && <p className="mt-1 text-[11px] text-slate-400" data-id="element-168" data-mp="pzlnbnaeqg4x">{helper}</p>}
    </div>;
}

/** Compact read-only value row for the loan context card. */
export function ReadOnlyField({
  label,
  value,
  strong = false
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  const empty = value.trim() === "";
  return <div className="flex flex-col gap-0.5" data-id="element-169" data-mp="18wee9cw3r4w3">
      <span className="text-xs font-medium text-slate-500" data-id="element-170" data-mp="18vlhn4quxvro">{label}</span>
      {empty ? <NeedsInfoPill data-id="element-171" data-mp="1dtwr3ed8q49d" /> : <span className={`text-sm ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`} data-id="element-172" data-mp="1dsv3kaf1fpar">
          {value}
        </span>}
    </div>;
}